/**
 * Markdown Renderer Component
 * Safely renders markdown content from AI responses
 */

import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { cn } from '@/lib/utils';

interface MarkdownRendererProps {
  content: string;
  className?: string;
}

/**
 * Renders markdown with proper styling and security
 * Handles: bold, italic, lists, line breaks, links, code blocks
 */
export const MarkdownRenderer = ({ content, className }: MarkdownRendererProps) => {
  return (
    <div className={cn(
      "prose prose-slate max-w-none",
      "prose-p:my-2 prose-p:leading-relaxed",
      "prose-strong:font-semibold prose-strong:text-slate-900",
      "prose-em:italic prose-em:text-slate-700",
      "prose-ul:my-2 prose-ul:list-disc prose-ul:pl-4",
      "prose-ol:my-2 prose-ol:list-decimal prose-ol:pl-4",
      "prose-li:my-1",
      "prose-code:bg-slate-100 prose-code:px-1 prose-code:py-0.5 prose-code:rounded prose-code:text-sm",
      "prose-pre:bg-slate-100 prose-pre:p-3 prose-pre:rounded-lg prose-pre:overflow-x-auto",
      "prose-a:text-blue-600 prose-a:underline hover:prose-a:text-blue-700",
      "prose-blockquote:border-l-4 prose-blockquote:border-slate-300 prose-blockquote:pl-4 prose-blockquote:italic",
      className
    )}>
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        components={{
          // Prevent dangerous HTML elements
          script: () => null,
          iframe: () => null,
          // Style links to be safe
          a: ({ node, ...props }) => (
            <a {...props} target="_blank" rel="noopener noreferrer" />
          ),
        }}
      >
        {content}
      </ReactMarkdown>
    </div>
  );
};

/**
 * Simple text renderer for plain text with line breaks
 * Use when content is guaranteed to be plain text
 */
export const PlainTextRenderer = ({ content, className }: MarkdownRendererProps) => {
  return (
    <p className={cn("text-slate-700 leading-relaxed whitespace-pre-wrap", className)}>
      {content}
    </p>
  );
};
